import { firstDefined, voidFunction } from '@avantia/client-and-server-utilities';
import { HpEither } from '@avantia/logging-and-errors';
import { HpButtonStyles, createControlId } from '@avantia/questionset-model';
import { SvgIcon } from '@avantia/style-guide';
import { HpClientQuestionLookupItem, HpClientStandardInputTextAndStyle } from '../../../types';
import { HpInputControlProps, HpOnMouseEvent } from '../../../types/inputControlTypes';
import { HpQuestionSetPageProps } from '../../../types/questionSetPagePropTypes';
import { HpCreateFormGroupCustomWidget, StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { createEqualityComparer, iterateLookupMap } from '../controlUtilities';
import { createStandardMouseEvent } from '../inputControl';

export interface CategorisedLookupContent {
  categoryLookupMap: string[];
  element: JSX.Element | null;
}

export type CreateCategorisedLookupFunc = (
  categorisedLookupMap: string[],
  option: HpClientQuestionLookupItem | null,
  key: string
) => CategorisedLookupContent;

export interface HpButtonListControlProps extends HpInputControlProps {
  dataOn?: string;
  dataOff?: string;
  createCategorisedLookup?: CreateCategorisedLookupFunc;
}

export interface HpCustomButtonContentFunctionProps {
  isSelected: boolean;
  lookupKey: string;
  optionText: string | null;
  pageProps: HpQuestionSetPageProps;
}

export type HpCustomButtonContentFunction = (props: HpCustomButtonContentFunctionProps) => JSX.Element;

export interface HpCustomButtonClassFunctionProps extends HpCustomButtonContentFunctionProps {
  className: string;
}

export type HpCustomButtonClassFunction = (props: HpCustomButtonClassFunctionProps) => string;

export interface HpCustomButtonContentProps extends HpCustomButtonCommonProps {
  createButtonContentCustom: HpCustomButtonContentFunction;
}

export interface HpCustomButtonProps extends HpCustomButtonCommonProps {
  createButtonCustom?: HpCustomButtonFunction;
  createLookupCustom?: (props: HpCreateLookupProps) => JSX.Element;
  createControlCustom?: (props: HpCreateControlProps) => JSX.Element;
  createFormGroupCustomWidget?: HpCreateFormGroupCustomWidget;
}

interface HpCustomButtonCommonProps {
  customButtonClass?: HpCustomButtonClassFunction;
}

interface HpCustomButtonControlProps {
  disabled?: boolean;
  name?: string;
}

export interface HpCustomButtonFunctionProps {
  className?: string;
  controlProps: HpCustomButtonControlProps;
  createButtonContentCustom?: HpCustomButtonContentFunction;
  itemKey: string;
  itemProps: HpCustomButtonContentFunctionProps;
  key: string;
  onClick: HpOnMouseEvent;
}

export type HpCustomButtonFunction = (props: HpCustomButtonFunctionProps) => JSX.Element;

export function ButtonListControl(props: HpButtonListControlProps): JSX.Element {
  const customProps: HpCustomButtonContentProps = {
    createButtonContentCustom: ({ optionText }: HpCustomButtonContentFunctionProps) => <>{optionText}</>,
    customButtonClass: ({ className }) => className
  };
  return ButtonListControlWithCustomContent(props, customProps);
}

export function ButtonCheckListControl(props: HpButtonListControlProps): JSX.Element {
  const customProps: HpCustomButtonProps = {
    createButtonCustom: CheckButton,
    customButtonClass: ({ className }) => `${className} hp-check-button`
  };
  return ButtonListControlWithCustomContent(props, customProps);
}

export function ButtonListControlWithCustomContent(
  props: HpButtonListControlProps,
  customProps: HpEither<HpCustomButtonContentProps, HpCustomButtonProps>
): JSX.Element {
  const {
    answerSource,
    createCategorisedLookup,
    data,
    dataType,
    errors,
    formatType,
    id,
    onChange,
    onClick,
    pageProps,
    question,
    questionText,
    subtext,
    ui,
    value
  } = props;
  const { isMultiSelect } = question;
  const { lookup } = data;
  const textAndStyle = data.textAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const error = errors ? errors[id] : undefined;
  const options: JSX.Element[] = [];
  const { layout } = textAndStyle;
  const isItemSelected = createEqualityComparer(dataType, isMultiSelect, id);
  const onButtonClick = createStandardMouseEvent(onClick, question);
  const { createControlCustom, createFormGroupCustomWidget, createLookupCustom } = customProps;

  if (lookup) {
    let classAlign = '';
    if (layout) {
      if (layout.endsWith('-left')) {
        classAlign = ' text-left';
      } else if (layout.endsWith('-right')) {
        classAlign = ' text-right';
      }
    }

    let consideredCategoryLookps: string[] = [];
    iterateLookupMap(lookup, ui, (option, key) => {
      const itemKey = createControlId({ questionId: id, key });
      const isSelected = isItemSelected(key, value);
      const button = createOptionButton({
        classAlign,
        isSelected,
        key,
        controlProps: props,
        itemKey,
        option,
        customProps,
        onButtonClick,
        pageProps
      });

      if (createCategorisedLookup) {
        const { element, categoryLookupMap } = createCategorisedLookup(
          consideredCategoryLookps,
          option,
          key
        ) as CategorisedLookupContent;
        if (element) {
          options.push(element);
        }

        consideredCategoryLookps = categoryLookupMap;
      }

      options.push((createLookupCustom || createLookupDefault)({ button, itemKey }));
    });
  }

  let groupClassName: string;
  switch (layout) {
    case 'vertical':
    case 'vertical-left':
      groupClassName = 'btn-group-vertical';
      break;
    case 'float':
      groupClassName = 'hp-float';
      break;
    default:
      groupClassName = 'btn-toolbar';
      break;
  }

  return (
    <StandardFormGroupWidget
      answerSource={answerSource}
      control={(createControlCustom || createControlDefault)({ groupClassName, options })}
      controlType="radio-button-list"
      createFormGroupCustomWidget={createFormGroupCustomWidget}
      dataType={dataType}
      error={error}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      id={id}
      onClick={onChange}
      question={question}
      questionText={questionText}
      subtext={subtext}
      textAndStyle={textAndStyle}
    />
  );
}

export interface HpCreateControlProps {
  groupClassName: string;
  options: JSX.Element[];
}

function createControlDefault({ groupClassName, options }: HpCreateControlProps): JSX.Element {
  return (
    <div className={groupClassName} role="group">
      {options}
    </div>
  );
}

export interface HpCreateLookupProps {
  button: JSX.Element;
  itemKey: string;
}

function createLookupDefault({ button, itemKey }: HpCreateLookupProps) {
  return (
    <div className="hp-btn-wrapper" key={itemKey}>
      {button}
    </div>
  );
}

function StandardButton({
  className,
  controlProps,
  createButtonContentCustom: ButtonContentCustom,
  itemKey,
  itemProps,
  key,
  onClick
}: HpCustomButtonFunctionProps): JSX.Element {
  const { disabled, name } = controlProps;
  const { optionText } = itemProps;
  const button = (
    <button
      id={itemKey}
      name={name || key}
      onClick={onClick as any}
      disabled={disabled === true}
      type="button"
      className={className}
      tabIndex={0}
    >
      {ButtonContentCustom ? <ButtonContentCustom {...itemProps} /> : <>{optionText}</>}
    </button>
  );
  return button;
}

export function CheckButton({
  className,
  controlProps,
  itemKey,
  itemProps,
  onClick
}: HpCustomButtonFunctionProps): JSX.Element {
  const { disabled } = controlProps;
  const { isSelected, optionText } = itemProps;
  return (
    <div id={itemKey} className={className} key={itemKey} onClick={disabled ? voidFunction : (onClick as any)}>
      <CheckButtonContent isSelected={isSelected} text={optionText} />
    </div>
  );
}

export interface HpCheckButtonProps {
  isSelected: boolean;
  text?: string | null;
}

export function CheckButtonContent({ isSelected, text }: HpCheckButtonProps): JSX.Element {
  const showLabel = text !== '';
  return (
    <div className="hp-select-box">
      {isSelected && (
        <>
          <div className="hp-selected-box">
            <SvgIcon type="check" />
          </div>
          {showLabel && <span>{text || 'Selected'}</span>}
        </>
      )}
      {!isSelected && (
        <>
          <div className="hp-unselected-box"></div>
          {showLabel && <span>{text || 'Select'}</span>}
        </>
      )}
    </div>
  );
}

interface HpCreateOptionButtonProps {
  classAlign: string;
  controlProps: HpButtonListControlProps;
  customProps: HpEither<HpCustomButtonContentProps, HpCustomButtonProps>;
  isSelected: boolean;
  itemKey: string;
  key: string;
  onButtonClick: HpOnMouseEvent;
  option: HpClientQuestionLookupItem | null;
  pageProps: HpQuestionSetPageProps;
}

function createOptionButton({
  classAlign,
  controlProps,
  customProps,
  isSelected,
  itemKey,
  key,
  onButtonClick,
  option,
  pageProps
}: HpCreateOptionButtonProps): JSX.Element {
  const optionText = firstDefined(option ? option.text : null, key);
  const buttonStyle: HpButtonStyles = isSelected ? 'primary' : 'outline-primary';
  const itemProps: HpCustomButtonContentFunctionProps = { optionText, lookupKey: key, isSelected, pageProps };
  const { createButtonContentCustom, createButtonCustom, customButtonClass } = customProps;

  let button: JSX.Element;
  const buttonProps: HpCustomButtonFunctionProps = {
    onClick: onButtonClick,
    className: '',
    controlProps,
    itemProps,
    itemKey,
    key,
    createButtonContentCustom
  };

  if (createButtonCustom) {
    if (customButtonClass) {
      buttonProps.className = customButtonClass({
        ...itemProps,
        className: buttonProps.className || ''
      });
    }

    button = createButtonCustom(buttonProps);
  } else {
    let className = `animated btn btn-${buttonStyle} animated pulse${classAlign}`;
    if (customButtonClass) {
      className = customButtonClass({ ...itemProps, className });
    }

    buttonProps.className = className;
    button = StandardButton(buttonProps);
  }

  return button;
}
